import { useCallback, useRef, useState } from 'react';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import Search from '@arcgis/core/widgets/Search';
import { useArcgisSketchLayer } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/useArcgisSketchLayer';
import { useLocationGraphicLayer } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/useLocationGraphicLayer';
import { HYBRID } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/arcGisTypes';
import { useLocalArcgisMapView } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/useLocalArcgisMapView';
import { useMapSearchWidget } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/useMapSearchWidget';
import { US_INITIAL_VIEW } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/constants';
import { zoomMapViewToGraphic } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/mapViewZoom';
import { useReverseGeocode } from 'components/GeoLocationComponent/useReverseGeocode';
import { useLocationFromArcgisGeometry } from 'components/GeoLocationComponent/useLocationFromArcgisGeometry';
import { MapToolPalette } from 'components/DesignSystems/MapToolPalette/MapToolPalette';
import { useGeoJsonImport } from 'components/GeoLocationComponent/useGeoJsonImport';
import type { GeoLocation } from 'components/GeoLocationComponent/GeoLocationComponent';
import type { Geometry } from 'geojson';

type IncidentGeoLocationPickerProps = {
  geometry?: Geometry;
  address?: string | null;
  onGeometryChange: (geometry: Geometry | null) => void;
  onAddressChange: (address: string | undefined) => void;
};

export const IncidentGeoLocationPicker: React.FC<
  IncidentGeoLocationPickerProps
> = ({ geometry, address, onGeometryChange, onAddressChange }) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<Search | null>(null);

  const [isMapActive, setIsMapActive] = useState(false);

  const { mapView } = useLocalArcgisMapView({
    isActive: isMapActive,
    containerRef: mapContainerRef,
    basemap: HYBRID,
    initialView: US_INITIAL_VIEW,
    onMapReady: (readyMapView) => {
      drawAndMaybeZoom(readyMapView);
    },
  });

  const reverseGeocode = useReverseGeocode();
  const getLocationFromArcgisGeometry =
    useLocationFromArcgisGeometry(reverseGeocode);

  const {
    drawingLayerRef: drawLayerRef,
    sketchViewModelRef,
    clearDrawingLayer: clearMapGraphics,
  } = useArcgisSketchLayer({
    mapView: isMapActive ? mapView : null,
    layerTitle: 'Incident Location',
    onCreateComplete: async ({ geometry: esriGeometry }) => {
      const geoLocation = await getLocationFromArcgisGeometry(esriGeometry);
      if (!geoLocation) return;

      const geoJson = geoLocation.geojson.data.features[0].geometry;
      onGeometryChange(geoJson);
      onAddressChange(geoLocation.address);
      drawLocationOnMap(geoJson, geoLocation.address);
    },
  });

  const { drawLocationOnMap } = useLocationGraphicLayer({
    mapView,
    drawingLayerRef: drawLayerRef,
    clearDrawingLayer: clearMapGraphics,
  });

  const drawAndMaybeZoom = (targetMapView: __esri.MapView | null) => {
    if (!targetMapView || !geometry) return;

    drawLocationOnMap(geometry, address ?? undefined, targetMapView);

    const graphic = drawLayerRef.current.graphics.getItemAt(0);
    if (!graphic) return;

    zoomMapViewToGraphic({ mapView: targetMapView, graphic });
  };

  const handleSearchSelectResult = useCallback(
    async (event: __esri.SearchSelectResultEvent) => {
      const resultGeometry = event.result.feature?.geometry;
      if (resultGeometry) {
        const geoLocation = await getLocationFromArcgisGeometry(
          resultGeometry,
          event.result.name
        );
        if (!geoLocation) return;

        const geoJson = geoLocation.geojson.data.features[0].geometry;
        onGeometryChange(geoJson);
        onAddressChange(geoLocation.address);
        drawLocationOnMap(geoJson, geoLocation.address);
      }
    },
    [
      drawLocationOnMap,
      getLocationFromArcgisGeometry,
      onAddressChange,
      onGeometryChange,
    ]
  );

  useMapSearchWidget({
    mapView,
    isActive: isMapActive,
    searchRef,
    onSelectResult: handleSearchSelectResult,
  });

  const onDrawPoint = useCallback(() => {
    sketchViewModelRef.current?.create('point');
  }, [sketchViewModelRef]);

  const onDrawPolygon = useCallback(() => {
    sketchViewModelRef.current?.create('polygon');
  }, [sketchViewModelRef]);

  const onClear = useCallback(() => {
    clearMapGraphics();
    onGeometryChange(null);
  }, [clearMapGraphics, onGeometryChange]);

  const { onImportFileChange } = useGeoJsonImport({
    setLocation: (geo: GeoLocation) => {
      onGeometryChange(geo.geojson.data.features[0].geometry);
      onAddressChange(geo.address ?? '');
    },
    setWatchLocation: () => {},
    drawingLayerRef: drawLayerRef,
    mapView: isMapActive ? mapView : null,
    reverseGeocode,
    drawLocationOnMap,
  });

  const openButtonLabel = geometry
    ? 'View/Edit Geolocation'
    : 'Edit Geolocation';

  return (
    <div className="mt-2">
      <div className="d-flex gap-2 mb-2">
        <StylishNewButton
          className="button--secondary"
          type="button"
          onClick={() => {
            if (!isMapActive) {
              drawAndMaybeZoom(mapView);
            }
            setIsMapActive((prev) => !prev);
          }}
        >
          {isMapActive ? 'Close Geolocation' : openButtonLabel}
        </StylishNewButton>
      </div>

      {isMapActive && (
        <div className="d-flex flex-column gap-2">
          <div
            className="border rounded overflow-hidden position-relative"
            style={{ height: '400px' }}
          >
            <div ref={mapContainerRef} className="h-100 w-100" />
            <MapToolPalette
              onDrawPoint={onDrawPoint}
              onDrawPolygon={onDrawPolygon}
              onClear={onClear}
              onImportFileChange={onImportFileChange}
            />
          </div>
        </div>
      )}
    </div>
  );
};
